import { useMemo, useState, useEffect } from 'react'
import { ComplementsAPI } from '../api'
import { Trash2, Copy, Edit, ArrowUpDown, ArrowUp, ArrowDown, Eye, EyeOff } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAdmin } from '../lib/hooks'
import { Pagination } from './Pagination'

const AdminComplements = () => {
    const { isAdmin, loading: adminLoading } = useAdmin()
    const navigate = useNavigate()

    const [message, setMessage] = useState<string | null>(null)
    const [items, setItems] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [query, setQuery] = useState('')
    const [currencyFilter, setCurrencyFilter] = useState<string>('ALL')
    const [payTypeFilter, setPayTypeFilter] = useState<string>('ALL')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({ key: 'sortOrder', direction: 'asc' })

    const load = async () => {
        setLoading(true)
        try {
            const res = await ComplementsAPI.list()
            setItems(res.data || [])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (isAdmin) load()
    }, [isAdmin])

    const goNew = () => navigate('/admin/new-complement')

    const onDelete = async (id: string) => {
        if (!window.confirm('¿Estás seguro de eliminar este complemento?')) return
        setMessage(null)
        await ComplementsAPI.delete(id)
        await load()
    }

    const formatMoney = (value: number, currency: string) => {
        try {
            return new Intl.NumberFormat('es-AR', {
                style: 'currency',
                currency,
                currencyDisplay: currency === 'USD' ? 'code' : 'symbol'
            }).format(value)
        } catch {
            return `${value} ${currency}`
        }
    }

    const filtered = items.filter((it) => {
        const q = query.trim().toLowerCase()
        const matchesQuery = q ? (it.name?.toLowerCase().includes(q) || it.displayName?.toLowerCase().includes(q)) : true
        const matchesCurrency = currencyFilter === 'ALL' ? true : it.currency === currencyFilter
        const matchesPayType = payTypeFilter === 'ALL' ? true : it.payType === payTypeFilter
        return matchesQuery && matchesCurrency && matchesPayType
    })

    const sortedItems = useMemo(() => {
        const sortableItems = [...filtered]
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key]
                let bValue = b[sortConfig.key]

                if (sortConfig.key === 'price' || sortConfig.key === 'sortOrder') {
                    aValue = Number(aValue || 0)
                    bValue = Number(bValue || 0)
                } else if (typeof aValue === 'string' && typeof bValue === 'string') {
                    aValue = aValue.toLowerCase()
                    bValue = bValue.toLowerCase()
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1
                }
                return 0
            })
        }
        return sortableItems
    }, [filtered, sortConfig])

    if (adminLoading) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Cargando permisos...</p></div>
    if (!isAdmin) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Acceso restringido</p></div>

    const total = items.length
    const filteredTotal = sortedItems.length
    const startIdx = (page - 1) * pageSize
    const endIdx = Math.min(startIdx + pageSize, filteredTotal)
    const pageItems = sortedItems.slice(startIdx, endIdx)

    const onSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc'
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc'
        }
        setSortConfig({ key, direction })
    }

    const onToggleVisibility = async (it: any) => {
        try {
            const newVisibility = it.isVisible === false ? true : false
            await ComplementsAPI.update(it.id, { isVisible: newVisibility })
            await load()
        } catch (error) {
            console.error('Error toggling visibility:', error)
        }
    }

    const onEdit = (it: any) => {
        navigate(`/admin/edit-complement/${it.id}`)
    }

    const onClone = (it: any) => {
        navigate('/admin/new-complement', { state: { cloneData: it } })
    }

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-24 mb-16">
            <div className="flex flex-col gap-4 mb-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl md:text-3xl font-bold">Administrar Complementos</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={() => navigate('/admin')}
                            className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 shadow-sm hover:bg-gray-200 transition-colors dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700"
                        >
                            Volver al Panel
                        </button>
                        <button
                            onClick={goNew}
                            className="px-4 py-2 rounded-md bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors"
                        >
                            Nuevo Complemento
                        </button>
                    </div>
                </div>
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="flex-1 min-w-[240px]">
                        <div className="relative">
                            <input
                                value={query}
                                onChange={(e) => { setQuery(e.target.value); setPage(1) }}
                                placeholder="Buscar complementos..."
                                className="w-full border rounded-md px-3 py-2 pl-9 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder-gray-400"
                            />
                            <span className="absolute left-3 top-2.5 text-gray-400 text-sm">🔍</span>
                        </div>
                    </div>
                    <select
                        className="border rounded-md px-3 py-2 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                        value={currencyFilter}
                        onChange={(e) => { setCurrencyFilter(e.target.value); setPage(1) }}
                    >
                        <option value="ALL">Todas las monedas</option>
                        <option value="USD">USD</option>
                        <option value="UYU">UYU</option>
                    </select>
                    <select
                        className="border rounded-md px-3 py-2 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                        value={payTypeFilter}
                        onChange={(e) => { setPayTypeFilter(e.target.value); setPage(1) }}
                    >
                        <option value="ALL">Todos los pagos</option>
                        <option value="ANUAL">Anual</option>
                        <option value="MENSUAL">Mensual</option>
                        <option value="UNICO">Único</option>
                        <option value="REFERIDO">Referido</option>
                    </select>
                    <div className="text-sm text-gray-500">
                        Mostrando {filteredTotal === 0 ? 0 : startIdx + 1}-{endIdx} de {filteredTotal} (filtradas de {total})
                    </div>
                </div>
            </div>
            {message && <p className="text-sm text-gray-700 mb-4">{message}</p>}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden dark:bg-slate-800 dark:border-slate-700">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-700">
                    <h3 className="text-lg font-semibold">Listado de Complementos</h3>
                </div>
                {loading ? (
                    <div className="px-5 py-8 text-gray-500">Cargando...</div>
                ) : (
                    <div className="px-5 py-4 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                            <thead className="bg-gray-50 dark:bg-slate-700/50">
                                <tr>
                                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 cursor-pointer select-none group" onClick={() => onSort('name')}>
                                        <div className="flex items-center gap-1">
                                            Nombre
                                            <span className="text-gray-400">
                                                {sortConfig?.key === 'name' ? (
                                                    sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                                                ) : (
                                                    <ArrowUpDown size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                                )}
                                            </span>
                                        </div>
                                    </th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 cursor-pointer select-none group" onClick={() => onSort('price')}>
                                        <div className="flex items-center justify-center gap-1">
                                            Precio
                                            <span className="text-gray-400">
                                                {sortConfig?.key === 'price' ? (
                                                    sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                                                ) : (
                                                    <ArrowUpDown size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                                )}
                                            </span>
                                        </div>
                                    </th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 cursor-pointer select-none group" onClick={() => onSort('sortOrder')}>
                                        <div className="flex items-center justify-center gap-1">
                                            Orden
                                            <span className="text-gray-400">
                                                {sortConfig?.key === 'sortOrder' ? (
                                                    sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                                                ) : (
                                                    <ArrowUpDown size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                                )}
                                            </span>
                                        </div>
                                    </th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3">Moneda</th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3">Pago</th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3">Estado</th>
                                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
                                {pageItems.map((it) => (
                                    <tr key={it.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                                        <td className="p-3">
                                            <div
                                                className="flex items-center gap-3 cursor-pointer group"
                                                onClick={() => onEdit(it)}
                                            >
                                                <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border border-gray-200 group-hover:border-blue-300 transition-colors dark:bg-slate-700 dark:border-slate-600">
                                                    {it.mainPhotoUrl ? (
                                                        <img src={it.mainPhotoUrl} alt="" className="h-full w-full object-contain p-1" />
                                                    ) : (
                                                        <span className="text-gray-400 font-bold text-xs dark:text-gray-500">{it.displayName?.substring(0, 2).toUpperCase() || 'CO'}</span>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors dark:text-white dark:group-hover:text-blue-400">{it.name}</div>
                                                    {it.displayName && <div className="text-xs text-gray-500 dark:text-gray-400">{it.displayName}</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3 text-center">
                                            <span className="font-medium text-gray-900 dark:text-white">{formatMoney(Number(it.price), it.currency || 'USD')}</span>
                                        </td>
                                        <td className="p-3 text-center">
                                            <span className="text-gray-600 font-medium dark:text-gray-400">{it.sortOrder || 0}</span>
                                        </td>
                                        <td className="p-3 text-center">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800">
                                                {it.currency}
                                            </span>
                                        </td>
                                        <td className="p-3 text-center">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800">
                                                {it.payType}
                                            </span>
                                        </td>
                                        <td className="p-3 text-center">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onToggleVisibility(it); }}
                                                className={`p-1 rounded-full transition-colors ${it.isVisible !== false ? 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                                                title={it.isVisible !== false ? "Ocultar" : "Mostrar"}
                                            >
                                                {it.isVisible !== false ? <Eye size={20} /> : <EyeOff size={20} />}
                                            </button>
                                        </td>
                                        <td className="p-3 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <button
                                                    onClick={() => onClone(it)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors dark:text-blue-400 dark:hover:bg-blue-900/30"
                                                    title="Clonar"
                                                >
                                                    <Copy size={18} />
                                                </button>
                                                <button
                                                    onClick={() => onEdit(it)}
                                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors dark:text-gray-400 dark:hover:bg-slate-700"
                                                    title="Editar"
                                                >
                                                    <Edit size={18} />
                                                </button>
                                                <button
                                                    onClick={() => onDelete(it.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors dark:text-red-400 dark:hover:bg-red-900/30"
                                                    title="Borrar"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {!loading && (
                    <Pagination
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        totalItems={filteredTotal}
                        className="bg-gray-50 dark:bg-slate-900/50 rounded-b-xl"
                    />
                )}
            </div>
        </div>
    )
}

export default AdminComplements
